import type { Brand } from './brand.ts';
import { dec, type Decimal, type DecimalInput, ROUND_CEIL, ROUND_HALF_UP } from './decimal.ts';

/** Деньги — целые копейки. */
export type Kopecks = Brand<number, 'Kopecks'>;
/** Время работ — целые минуты, не отрицательные. */
export type Minutes = Brand<number, 'Minutes'>;
/** Проценты и коэффициенты — целые базисные пункты; шкала 10 000 (1% = 100, ×1,3 = 13 000). */
export type BasisPoints = Brand<number, 'BasisPoints'>;

export const BP_SCALE = 10_000;

function assertSafeInteger(value: number, what: string): void {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${what}: ожидалось безопасное целое, получено ${String(value)}`);
  }
}

function integerFromDecimal(value: Decimal, what: string): number {
  if (!value.isInteger()) {
    throw new RangeError(`${what}: значение ${value.toString()} не целое`);
  }
  const n = value.toNumber();
  assertSafeInteger(n, what);
  return n;
}

export function kopecks(value: number): Kopecks {
  assertSafeInteger(value, 'Копейки');
  return value as Kopecks;
}

/** Рубли (число или строка с точкой) → копейки. Дробь меньше копейки — ошибка. */
export function kopecksFromRubles(rubles: DecimalInput): Kopecks {
  return integerFromDecimal(dec(rubles).times(100), 'Копейки') as Kopecks;
}

export function sumKopecks(values: Iterable<Kopecks>): Kopecks {
  let total = 0;
  for (const v of values) total += v;
  return kopecks(total);
}

export function minutes(value: number): Minutes {
  assertSafeInteger(value, 'Минуты');
  if (value < 0) throw new RangeError(`Минуты: отрицательное значение ${String(value)}`);
  return value as Minutes;
}

export function minutesFromHours(hours: DecimalInput): Minutes {
  return minutes(integerFromDecimal(dec(hours).times(60), 'Минуты'));
}

export function sumMinutes(values: Iterable<Minutes>): Minutes {
  let total = 0;
  for (const v of values) total += v;
  return minutes(total);
}

export function bp(value: number): BasisPoints {
  assertSafeInteger(value, 'Базисные пункты');
  return value as BasisPoints;
}

/** Проценты → базисные пункты: 6 → 600, 47,02 → 4702. */
export function bpFromPercent(percent: DecimalInput): BasisPoints {
  return bp(integerFromDecimal(dec(percent).times(100), 'Базисные пункты'));
}

/** Базисные пункты → десятичная доля: 600 → 0,06; 13 000 → 1,3. */
export function bpToDecimal(value: BasisPoints): Decimal {
  return dec(value).div(BP_SCALE);
}

/** Округление до целой копейки, половина — от нуля. */
export function roundHalfUpToKopeck(value: Decimal): Kopecks {
  return kopecks(value.toDecimalPlaces(0, ROUND_HALF_UP).toNumber());
}

function assertStep(step: Kopecks): void {
  if (step <= 0) throw new RangeError(`Шаг округления должен быть положительным, получено ${String(step)}`);
}

/** Округление до кратного шагу, половина — от нуля. */
export function roundHalfUpToStep(value: Decimal, step: Kopecks): Kopecks {
  assertStep(step);
  return kopecks(value.div(step).toDecimalPlaces(0, ROUND_HALF_UP).times(step).toNumber());
}

/** Округление вверх до кратного шагу. */
export function ceilToStep(value: Decimal, step: Kopecks): Kopecks {
  assertStep(step);
  return kopecks(value.div(step).toDecimalPlaces(0, ROUND_CEIL).times(step).toNumber());
}
